// src/components/admin/mail/AdminMail.tsx
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { AlertCircle, PenSquare } from 'lucide-react';
import { ComposeMode, MailAccount } from '../../../types/mail';
import { getAccounts } from './mailApi';
import { accentFor, useMediaQuery } from './mailUtils';
import { useMailbox } from './useMailbox';
import MailAccountTabs from './MailAccountTabs';
import MailComposeModal from './MailComposeModal';
import MailConfirmDialog from './MailConfirmDialog';
import MailFolderRail from './MailFolderRail';
import MailMessageDetail from './MailMessageDetail';
import MailMessageList from './MailMessageList';
import {
    AccentBar,
    Button,
    DetailPane,
    ListPane,
    MailWorkspace,
    SkeletonBar,
} from './mail.styles';

const Container = styled.div`
    width: 100%;
    box-sizing: border-box;
`;

const TitleRow = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;
`;

const Title = styled.h1`
    font-size: 1.8rem;
    font-weight: 700;
    background: linear-gradient(135deg, #333, #990147);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    margin: 0 0 0.4rem 0;
`;

const Subtitle = styled.p`
    margin: 0 0 1.5rem 0;
    color: #64748b;
    font-size: 0.92rem;
`;

const Notice = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.7rem;
    padding: 1.1rem 1.25rem;
    background: #fffbeb;
    border: 1px solid #fde68a;
    border-radius: 12px;
    color: #92400e;
    font-size: 0.9rem;
    line-height: 1.55;
    box-sizing: border-box;
`;

const LoadingBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.8rem;
    padding: 2rem;
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    box-sizing: border-box;
`;

const SingleColumn = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    flex: 1;
`;

/**
 * The "Pošta" section of the admin panel: read, search, reply, forward, compose and
 * delete across Inbox / Sent / Trash for each configured account.
 */
const AdminMail: React.FC = () => {
    const [accounts, setAccounts] = useState<MailAccount[]>([]);
    const [accountsLoaded, setAccountsLoaded] = useState(false);
    const [activeId, setActiveId] = useState<string | null>(null);
    const [composeMode, setComposeMode] = useState<ComposeMode | null>(null);
    const [confirmDelete, setConfirmDelete] = useState(false);

    // 1100, not 1024: the shell sidebar is 280px and does not shrink until 768px.
    const isNarrow = useMediaQuery('(max-width: 1099px)');
    const reduceMotion = useReducedMotion();

    const mailbox = useMailbox(activeId);
    const activeAccount = accounts.filter((a) => a.id === activeId)[0] || null;
    const accent = accentFor(activeId || 'work');
    const activeFolder = mailbox.folders.filter((f) => f.id === mailbox.folder)[0];

    // Restores the last mailbox after the AnimatePresence remount that happens whenever she
    // switches to Blog or Klienti and back.
    useEffect(() => {
        let cancelled = false;
        getAccounts()
            .then((data) => {
                if (cancelled) return;
                setAccounts(data);
                const remembered = window.localStorage.getItem('mail:activeAccount');
                const exists = data.filter((a) => a.id === remembered)[0];
                setActiveId(exists ? exists.id : data.length > 0 ? data[0].id : null);
            })
            .catch(() => {
                if (!cancelled) setAccounts([]);
            })
            .finally(() => {
                if (!cancelled) setAccountsLoaded(true);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const handleAccountChange = (id: string) => {
        setActiveId(id);
        window.localStorage.setItem('mail:activeAccount', id);
    };

    const refreshAccounts = () => {
        getAccounts().then(setAccounts).catch(() => undefined);
    };

    if (!accountsLoaded) {
        return (
            <Container>
                <Title>Pošta</Title>
                <LoadingBox>
                    <SkeletonBar $width="40%" $height="16px" />
                    <SkeletonBar $width="70%" />
                    <SkeletonBar $width="55%" />
                </LoadingBox>
            </Container>
        );
    }

    if (accounts.length === 0) {
        return (
            <Container>
                <Title>Pošta</Title>
                <Subtitle>Čítanie a odpovedanie na e-maily priamo v administrácii.</Subtitle>
                <Notice>
                    <AlertCircle size={20} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>
                        <strong>Zatiaľ nie je nastavená žiadna schránka.</strong>
                        <br />
                        Schránku je potrebné zapnúť na serveri (premenné prostredia{' '}
                        <code>MAIL_WORK_*</code> pre ambulanciu a <code>MAIL_GMAIL_*</code> pre Gmail).
                        Pošta sa tu zobrazí hneď po nastavení.
                    </span>
                </Notice>
            </Container>
        );
    }

    const listPane = (
        <MailMessageList
            accountId={activeId as string}
            folderLabel={activeFolder ? activeFolder.displayName : 'Doručená pošta'}
            messages={mailbox.messages}
            listStatus={mailbox.listStatus}
            selectedUid={mailbox.selectedUid}
            uidValidity={mailbox.uidValidity}
            accent={accent.solid}
            page={mailbox.page}
            totalPages={mailbox.totalPages}
            totalElements={mailbox.totalElements}
            search={mailbox.search}
            isSearching={mailbox.isSearching}
            isRefreshing={mailbox.isRefreshing}
            lastSyncedAt={mailbox.lastSyncedAt}
            onSearchChange={mailbox.setSearch}
            onOpen={mailbox.openMessage}
            onPageChange={mailbox.setPage}
            onRefresh={mailbox.refresh}
        />
    );

    const detailPane = (
        <MailMessageDetail
            accountId={activeId as string}
            folder={mailbox.folder}
            detail={mailbox.detail}
            detailStatus={mailbox.detailStatus}
            imagesAllowed={mailbox.imagesAllowed}
            onReply={() => setComposeMode('reply')}
            onReplyAll={() => setComposeMode('replyAll')}
            onForward={() => setComposeMode('forward')}
            onDelete={() => setConfirmDelete(true)}
            onMarkUnread={() => {
                if (mailbox.detail) {
                    mailbox.setSeen(mailbox.detail.uid, false);
                    mailbox.closeMessage();
                }
            }}
            onShowImages={mailbox.showRemoteImages}
            onRetry={() => mailbox.selectedUid !== null && mailbox.openMessage(mailbox.selectedUid)}
            onBack={isNarrow ? mailbox.closeMessage : undefined}
        />
    );

    const slide = reduceMotion
        ? {}
        : { initial: { opacity: 0, x: 60 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: 60 } };

    return (
        <Container>
            <TitleRow>
                <div>
                    <Title>Pošta</Title>
                    <Subtitle>Čítanie a odpovedanie na e-maily priamo v administrácii.</Subtitle>
                </div>
                <Button type="button" onClick={() => setComposeMode('new')} whileTap={{ scale: 0.97 }}>
                    <PenSquare size={16} />
                    Nová správa
                </Button>
            </TitleRow>

            <MailAccountTabs accounts={accounts} activeId={activeId} onChange={handleAccountChange} />

            {activeAccount && !activeAccount.healthy && (
                <Notice style={{ marginBottom: '1rem' }}>
                    <AlertCircle size={20} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>{activeAccount.healthMessage || 'Schránka je momentálne nedostupná.'}</span>
                </Notice>
            )}

            <AccentBar $gradient={accent.gradient} />
            <MailWorkspace>
                <MailFolderRail
                    folders={mailbox.folders}
                    activeFolder={mailbox.folder}
                    accent={accent.solid}
                    onSelect={mailbox.setFolder}
                />

                {isNarrow ? (
                    <SingleColumn>
                        <AnimatePresence mode="wait">
                            {mailbox.selectedUid !== null ? (
                                <motion.div
                                    key="detail"
                                    {...slide}
                                    transition={{ duration: 0.25 }}
                                    style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
                                >
                                    {detailPane}
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="list"
                                    {...(reduceMotion
                                        ? {}
                                        : {
                                              initial: { opacity: 0, x: -60 },
                                              animate: { opacity: 1, x: 0 },
                                              exit: { opacity: 0, x: -60 },
                                          })}
                                    transition={{ duration: 0.25 }}
                                    style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
                                >
                                    {listPane}
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </SingleColumn>
                ) : (
                    <>
                        <ListPane>{listPane}</ListPane>
                        <DetailPane>{detailPane}</DetailPane>
                    </>
                )}
            </MailWorkspace>

            {activeAccount && composeMode && (
                <MailComposeModal
                    open
                    mode={composeMode}
                    account={activeAccount}
                    accent={accent}
                    folder={mailbox.folder}
                    source={composeMode === 'new' ? null : mailbox.detail}
                    onClose={() => setComposeMode(null)}
                    onSent={() => {
                        setComposeMode(null);
                        mailbox.refresh();
                        refreshAccounts();
                    }}
                />
            )}

            <MailConfirmDialog
                open={confirmDelete}
                title={
                    mailbox.detail
                        ? `Zmazať správu „${mailbox.detail.subject}“?`
                        : 'Zmazať správu?'
                }
                message={
                    mailbox.folder === 'trash'
                        ? 'Správa sa zmaže natrvalo. Túto akciu už nie je možné vrátiť späť.'
                        : 'Správa sa presunie do koša. Obnoviť ju môžete v priečinku Kôš.'
                }
                confirmLabel={mailbox.folder === 'trash' ? 'Zmazať natrvalo' : 'Presunúť do koša'}
                danger
                onConfirm={() => {
                    if (mailbox.detail) mailbox.removeMessage(mailbox.detail.uid);
                    setConfirmDelete(false);
                }}
                onCancel={() => setConfirmDelete(false)}
            />
        </Container>
    );
};

export default AdminMail;
